import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { KlaroWordmark } from "./components/KlaroWordmark";

const API = process.env.REACT_APP_API_URL || "/api";

export default function AuthPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetch(`${API}/company-profile/public`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setProfile(data); })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const kalsiLogo = profile?.logo_base64 || null;
  const tagline = profile?.tagline || null;

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: "linear-gradient(150deg, hsl(var(--klaro-paper)) 0%, hsl(var(--klaro-mist)) 100%)" }}
    >
      <div className="w-full max-w-sm">

        {/* ── Brand lockup ── */}
        <div className="text-center mb-8">
          {/* Primary: Klaro wordmark */}
          <KlaroWordmark size="xl" theme="light" style={{ display: "block", marginBottom: "0.5rem" }} />

          {/* Secondary: "by Kalsi" lockup — smaller, clearly subordinate */}
          <div className="flex items-center justify-center gap-2 mt-1">
            <span
              style={{
                fontFamily: "'Inter', Arial, sans-serif",
                fontSize: "0.8125rem",
                fontWeight: 500,
                color: "hsl(var(--klaro-slate))",
                letterSpacing: "0.01em",
              }}
            >
              by Kalsi
            </span>
            {kalsiLogo && (
              <img
                src={kalsiLogo}
                alt="Kalsi"
                style={{ height: "1.5rem", width: "auto", objectFit: "contain", opacity: 0.75 }}
              />
            )}
          </div>

          {/* Tagline — shown only if set in company profile */}
          {tagline && (
            <p
              className="mt-2 text-xs"
              style={{ color: "hsl(var(--klaro-slate))", opacity: 0.7 }}
            >
              {tagline}
            </p>
          )}
        </div>

        {/* ── Sign-in card ── */}
        <div
          className="bg-card rounded-xl shadow-md p-6 md:p-8"
          style={{ border: "1px solid hsl(var(--border))" }}
        >
          <h2
            className="text-lg font-semibold mb-1 text-card-foreground"
          >
            Sign in
          </h2>
          <p className="text-sm mb-6 text-muted-foreground">
            Sign in to your linen management portal.
          </p>

          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm"
              style={{
                backgroundColor: "hsl(var(--destructive) / 0.1)",
                border: "1px solid hsl(var(--destructive) / 0.3)",
                color: "hsl(var(--destructive))",
              }}
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5 text-card-foreground">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg outline-none text-sm transition-colors"
                style={{
                  border: "1px solid hsl(var(--border))",
                  color: "hsl(var(--card-foreground))",
                  backgroundColor: "hsl(var(--background))",
                }}
                onFocus={e => {
                  e.target.style.borderColor = "hsl(var(--klaro-clarity-blue))";
                  e.target.style.boxShadow = "0 0 0 3px hsl(var(--klaro-clarity-blue) / 0.15)";
                }}
                onBlur={e => {
                  e.target.style.borderColor = "hsl(var(--border))";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="you@example.com"
                autoComplete="email"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1.5 text-card-foreground">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 rounded-lg outline-none text-sm transition-colors"
                style={{
                  border: "1px solid hsl(var(--border))",
                  color: "hsl(var(--card-foreground))",
                  backgroundColor: "hsl(var(--background))",
                }}
                onFocus={e => {
                  e.target.style.borderColor = "hsl(var(--klaro-clarity-blue))";
                  e.target.style.boxShadow = "0 0 0 3px hsl(var(--klaro-clarity-blue) / 0.15)";
                }}
                onBlur={e => {
                  e.target.style.borderColor = "hsl(var(--border))";
                  e.target.style.boxShadow = "none";
                }}
                placeholder="Enter your password"
                autoComplete="current-password"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 rounded-lg font-medium disabled:opacity-50 transition-colors text-sm mt-2"
              style={{
                backgroundColor: "hsl(var(--klaro-blue))",
                color: "hsl(var(--primary-foreground))",
              }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.backgroundColor = "hsl(var(--klaro-deep-blue))"; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = "hsl(var(--klaro-blue))"; }}
            >
              {loading ? "Please wait…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-center text-xs text-muted-foreground">
            Contact your administrator to create an account.
          </p>
        </div>
      </div>
    </div>
  );
}
